import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment
} from "@material-ui/core";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Visibility from "@material-ui/icons/Visibility";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  otpContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  otpInput: {
    width: '40px',
    height: '40px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-focused': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'center',
      fontSize: '1.2rem',
      fontWeight: '500',
      padding: theme.spacing(1),
    },
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: '500',
    marginBottom: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  eyeButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
  },
}));

const VerificationMfaCode = ({ onVerify, loading, loginError }) => {
  const classes = useStyles();
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const inputRefs = useRef([]);

  // Check if all 6 digits are filled
  const isOtpComplete = () => {
    return mfaCode.every(digit => digit !== '');
  };

  // Check if form is valid
  const isFormValid = () => {
    return isOtpComplete();
  };

  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...mfaCode];
    newOtp[index] = value;
    setMfaCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    // Handle backspace
    if (event.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...mfaCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setMfaCode(newOtp);
    
    // Focus the next empty field or last field
    const nextEmptyIndex = newOtp.findIndex((digit, index) => !digit && index < 6);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setHasTriedSubmit(true);
    
    if (!isFormValid()) {
      return;
    }
    
    const otpString = mfaCode.join('');
    onVerify(otpString);
  };

  const toggleShowOtp = () => {
    setShowOtp(!showOtp);
  };

  return (
    <div className={classes.container}>
      <Typography className={classes.title}>
        Enter Verification Code
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box className={classes.otpContainer}>
          {mfaCode.map((digit, index) => (
            <TextField
              key={index}
              inputRef={el => inputRefs.current[index] = el}
              variant="outlined"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              type={showOtp ? "text" : "password"}
              inputProps={{
                maxLength: 1,
              }}
              error={hasTriedSubmit && !digit}
              className={classes.otpInput}
            />
          ))}
          <IconButton
            onClick={toggleShowOtp}
            className={classes.eyeButton}
            size="small"
          >
            {showOtp ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </Box>

        {hasTriedSubmit && !isFormValid() && (
          <Typography className={classes.errorText}>
            Please enter all 6 digits
          </Typography>
        )}


        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isFormValid() || loading}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
      </form>
    </div>
  );
};

export default VerificationMfaCode;
